/**
 * Phone Validation Logic - SHARED
 * 
 * Works on both frontend and backend with the same logic.
 */

import { SMS_CONFIG, getSupportedCountries, getCountryConfig } from './config';

export interface PhoneValidationResult {
  isValid: boolean;
  error?: string;
  countryCode?: string;
  formatted?: string;
}

/**
 * Normalize phone number by removing formatting characters
 */
export function normalizePhoneNumber(phone: string): string {
  return phone.replace(/[\s\-().]/g, '');
}

/**
 * Validate phone number for a specific country
 */
export function validatePhoneNumber(
  phone: string,
  countryCode: string,
  options: { mobileOnly?: boolean } = {}
): PhoneValidationResult {
  if (!phone || typeof phone !== 'string') {
    return { isValid: false, error: 'Phone number is required' };
  }

  const normalized = normalizePhoneNumber(phone.trim());
  
  if (normalized.length === 0) {
    return { isValid: false, error: 'Phone number cannot be empty' };
  }

  const config = getCountryConfig(countryCode);
  if (!config) {
    return { isValid: false, error: `Country ${countryCode} not configured` };
  }

  // Choose pattern based on options
  const patternString = options.mobileOnly && config.mobilePattern 
    ? config.mobilePattern 
    : config.pattern;
  
  const pattern = new RegExp(patternString);
  const isValid = pattern.test(normalized);

  if (!isValid) {
    return {
      isValid: false,
      error: `Invalid ${config.name} phone number. Expected: ${config.example}`,
      countryCode
    };
  }

  return { isValid: true, countryCode, formatted: normalized };
}

/**
 * Validate phone number for SMS notifications
 * Tries all supported countries
 */
export function validatePhoneForSms(phone: string): PhoneValidationResult {
  if (!phone || typeof phone !== 'string') {
    return { isValid: false, error: 'Phone number is required' };
  }

  const cleanPhone = phone.trim();
  const supportedCountries = getSupportedCountries();

  // In strict mode, validate against country patterns
  if (SMS_CONFIG.validationMode === 'strict') {
    // Try each supported country
    for (const countryCode of supportedCountries) {
      const result = validatePhoneNumber(cleanPhone, countryCode);
      if (result.isValid) {
        return result;
      }
    }

    // If strict mode and fallback enabled, try E.164
    if (SMS_CONFIG.fallbackToE164) {
      const e164Pattern = new RegExp(SMS_CONFIG.e164Pattern);
      if (e164Pattern.test(cleanPhone)) {
        return { isValid: true, countryCode: 'UNKNOWN' };
      }
    }

    // Build error message with supported formats
    const examples = supportedCountries
      .map(code => getCountryConfig(code)?.example)
      .filter(Boolean)
      .join(' or ');

    return {
      isValid: false,
      error: `Invalid phone number format. Expected: ${examples}`
    };
  }

  // In lenient mode, just check E.164
  const e164Pattern = new RegExp(SMS_CONFIG.e164Pattern);
  if (e164Pattern.test(cleanPhone)) {
    return { isValid: true };
  }

  return {
    isValid: false,
    error: 'Invalid phone number format. Must be E.164 format (e.g., +263779325860)'
  };
}

/**
 * Validate phone number accepting multiple country codes
 * Tries to validate against each country until one succeeds
 * If no countries provided, uses all supported countries
 */
export function validatePhoneMultiCountry(
  phone: string,
  countryCodes?: string[],
  options: { mobileOnly?: boolean } = {}
): PhoneValidationResult {
  // Use provided countries or all supported countries
  const countriesToTry = countryCodes || getSupportedCountries();
  
  // Try each country code until we find a match
  for (const countryCode of countriesToTry) {
    const result = validatePhoneNumber(phone, countryCode, options);
    if (result.isValid) {
      return result;
    }
  }
  
  // If no country matched, return validation error with all supported formats
  const examples = countriesToTry
    .map(code => getCountryConfig(code)?.example)
    .filter(Boolean)
    .join(' or ');
  
  return {
    isValid: false,
    error: `Invalid phone number format. Expected format: ${examples}`
  };
}

/**
 * Convert phone number to international format
 */
export function toInternationalFormat(phone: string, countryCode: string = 'ZW'): string {
  const config = getCountryConfig(countryCode);
  if (!config) return phone;

  const normalized = normalizePhoneNumber(phone);
  
  // If already international, return as-is
  if (normalized.startsWith('+')) {
    return normalized;
  }

  // Convert local format to international
  if (countryCode === 'ZW' && normalized.startsWith('0')) {
    return `+263${normalized.substring(1)}`;
  }
  
  if (countryCode === 'DE' && normalized.startsWith('0')) {
    return `+49${normalized.substring(1)}`;
  }

  // If country has dialing code, prepend it
  if (config.dialingCode && !normalized.startsWith('+')) {
    return `${config.dialingCode}${normalized}`;
  }

  return phone;
}

